import { formatDateDdMmYyyy } from './bankDetailsRemindersError';

export const TRIGGERED_RECAP_CODES = {
    SENT: 'TRIGGERED_RECAP_EMAILS_SENT',
    NO_CLIENTS: 'NO_ELIGIBLE_CLIENTS',
    INVALID: 'INVALID_REQUEST',
    COMPANY_NOT_FOUND: 'COMPANY_NOT_FOUND',
    INTERNAL: 'INTERNAL_SERVER_ERROR',
};

export const TRIGGERED_RECAP_ROW_STATUS = {
    SENT: 'sent',
    SKIPPED: 'skipped',
    FAILED: 'failed',
};

export const END_OF_STAY_RECAP_CODES = {
    SENT: 'END_OF_STAY_RECAP_EMAILS_SENT',
    NO_CLIENTS: 'NO_ELIGIBLE_CLIENTS',
    INVALID: 'INVALID_REQUEST',
    COMPANY_NOT_FOUND: 'COMPANY_NOT_FOUND',
    INTERNAL: 'INTERNAL_SERVER_ERROR',
};

const RECAP_SUCCESS_CODES = new Set([
    TRIGGERED_RECAP_CODES.SENT,
    END_OF_STAY_RECAP_CODES.SENT,
]);

export const isTriggeredRecapResponse = (result) => (
    result != null
    && typeof result === 'object'
    && typeof result.code === 'string'
);

export const isRecapSuccessCode = (code) => (
    RECAP_SUCCESS_CODES.has(code) || code === TRIGGERED_RECAP_CODES.NO_CLIENTS
);

const normalizeTriggeredRecapDetailRow = (row) => {
    if (!row || typeof row !== 'object') return null;

    const policyInfo = row.policy_info || {};
    const clientInfo = row.client_info || {};
    const activityInfo = row.activity_info || {};
    const partnerInfo = row.partner_info || {};
    const amountsSent = row.amounts_sent || {};

    // Format Standard plat (policy_code / reason / email_code) ou Flower imbriqué.
    const policyCode = policyInfo.policy_code ?? row.policy_code ?? null;
    const partnerId = partnerInfo.partner_id ?? row.partner_id ?? null;

    return {
        status: row.status || null,
        reason: row.reason ?? null,
        emailCode: row.email_code ?? row.emailCode ?? null,
        emailListGate: row.email_list_gate ?? row.emailListGate ?? null,
        triggered: typeof row.triggered === 'boolean' ? row.triggered : null,
        partnerId,
        policyInfo: {
            policyId: policyInfo.policy_id ?? row.policy_id ?? null,
            policyCode,
            status: policyInfo.status ?? null,
            preferredLanguage: policyInfo.preferred_language ?? null,
        },
        clientInfo: {
            clientId: clientInfo.client_id ?? null,
            firstName: clientInfo.first_name ?? '',
            lastName: clientInfo.last_name ?? '',
            email: clientInfo.email ?? '',
        },
        activityInfo: {
            reservationNumber: activityInfo.reservation_number ?? null,
            activityName: activityInfo.activity_name ?? null,
            newTripEndDate: activityInfo.new_trip_end_date ?? null,
        },
        partnerInfo: {
            companyName: partnerInfo.company_name ?? null,
        },
        amountsSent: {
            cashTotal: amountsSent.cash_total ?? null,
            voucherTotal: amountsSent.voucher_total ?? null,
            claimTotal: amountsSent.claim_total ?? null,
            cashTotalEn: amountsSent.cash_total_en ?? null,
            voucherTotalEn: amountsSent.voucher_total_en ?? null,
            claimTotalEn: amountsSent.claim_total_en ?? null,
        },
        message: row.message ?? row.reason ?? null,
    };
};

export const normalizeTriggeredRecapResult = (result) => {
    if (!isTriggeredRecapResponse(result)) return null;

    const summary = result.summary || {};
    const filters = result.filters || {};
    const details = Array.isArray(result.details)
        ? result.details.map(normalizeTriggeredRecapDetailRow).filter(Boolean)
        : [];

    return {
        success: result.success === true,
        code: result.code,
        message: result.message || '',
        timestamp: result.timestamp || null,
        summary: {
            eligibleClients: summary.eligible_clients ?? 0,
            emailsSent: summary.emails_sent ?? 0,
            emailsSkipped: summary.emails_skipped ?? 0,
            emailsSkippedEmailList: summary.emails_skipped_email_list ?? 0,
            emailsFailed: summary.emails_failed ?? 0,
            emailsTriggered: summary.emails_triggered ?? null,
            emailsNotTriggered: summary.emails_not_triggered ?? null,
        },
        filters: {
            tripEndDateFrom: filters.trip_end_date_from ?? null,
            tripEndDateTo: filters.trip_end_date_to ?? null,
            companyName: filters.company_name ?? null,
        },
        details,
        error: result.error || null,
    };
};

export const getTriggeredRecapAlertVariant = (normalized) => {
    if (!normalized) return 'secondary';
    switch (normalized.code) {
        case TRIGGERED_RECAP_CODES.SENT:
        case END_OF_STAY_RECAP_CODES.SENT:
            if (normalized.summary.emailsFailed > 0) return 'warning';
            if (
                normalized.summary.emailsSent === 0
                && (normalized.summary.emailsSkipped > 0 || normalized.summary.emailsSkippedEmailList > 0)
            ) {
                return 'warning';
            }
            return 'success';
        case TRIGGERED_RECAP_CODES.NO_CLIENTS:
            return 'info';
        case TRIGGERED_RECAP_CODES.INVALID:
            return 'warning';
        default:
            return 'danger';
    }
};

export const formatTriggeredRecapDate = (value) => formatDateDdMmYyyy(value);

export const parseTriggeredRecapApiError = (error) => {
    const data = error?.response?.data;
    if (isTriggeredRecapResponse(data)) return data;
    return null;
};

export const getTriggeredRecapFieldError = (result) => {
    if (!result || result.code !== TRIGGERED_RECAP_CODES.INVALID) return null;
    const field = result.error?.field;
    const reason = result.error?.reason;
    if (!field || !reason) return null;
    return { field, reason };
};

export const getTriggeredRecapRowStatusBadgeVariant = (status) => {
    switch (status) {
        case TRIGGERED_RECAP_ROW_STATUS.SENT:
            return 'success';
        case TRIGGERED_RECAP_ROW_STATUS.SKIPPED:
            return 'secondary';
        case TRIGGERED_RECAP_ROW_STATUS.FAILED:
            return 'danger';
        default:
            return 'secondary';
    }
};

export const buildTriggeredRecapExcelRows = (details, { labels, translateStatus, formatReason }) => {
    if (!Array.isArray(details) || details.length === 0) return [];

    return details.map((row) => {
        const clientName = `${row.clientInfo?.firstName || ''} ${row.clientInfo?.lastName || ''}`.trim();
        const reasonText = typeof formatReason === 'function'
            ? formatReason(row.reason || row.message || row.emailCode)
            : (row.reason || row.message || '');
        return {
            [labels.policyCode]: row.policyInfo?.policyCode || '',
            [labels.reservationNumber]: row.activityInfo?.reservationNumber || '',
            [labels.client]: clientName,
            [labels.email]: row.clientInfo?.email || '',
            [labels.companyName]: row.partnerInfo?.companyName || '',
            [labels.tripEndDate]: row.activityInfo?.newTripEndDate || '',
            [labels.claimTotal]: row.amountsSent?.claimTotal || '',
            [labels.cashTotal]: row.amountsSent?.cashTotal || '',
            [labels.voucherTotal]: row.amountsSent?.voucherTotal || '',
            [labels.status]: translateStatus(row.status),
            [labels.emailCode]: row.emailCode || '',
            [labels.triggered]: row.triggered == null ? '' : (row.triggered ? 'true' : 'false'),
            [labels.partnerId]: row.partnerId || '',
            [labels.message]: reasonText,
        };
    });
};
